/**
 * Reconciliation state and handlers for the invoice detail page (NEX matching, modals, flags).
 * Depends on form state for getLineItemStringValue, lineItemBackendIndexByCurrentIndex, etc.
 * Kept under 750 lines.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import type { InvoiceDetailResponse, InvoicePart, NexDetailLine, ReconciliationMatchNote } from '../../types/invoice';
import { fmtUsd } from './detailForm/invoiceDetailFormFormatters';
import { resolveTabularLocationForGlobalPartIndex } from '../../utils/invoiceGlobalPartIndex';
import { buildLineItemExpandKey } from './invoiceDetailFormMappings';
import { findNexPoVendorMetaForName } from '../../utils/invoiceVendorDisplay';
import {
    isIssueTypeFlaggingEnabled,
    omitReturnSlipAmountFlags,
    type InvoiceMismatchFlagFamilyPolicy,
} from '../../utils/invoiceFlagFamilyPolicy';

export type ReconciliationDetailModalState = {
    type: 'flag' | 'auto' | 'match_note';
    partIndex?: number;
    issue?: any;
    correction?: any;
    matchNote?: { invoicePartNumber: string; nexPartNumber: string; reason: string; partIndex?: number };
} | null;

export interface FormStateForReconciliation {
    editedFields: Partial<InvoiceDetailResponse>;
    updateLineItem: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], value: any) => void;
    updateField: (field: keyof InvoiceDetailResponse, value: any) => void;
    getLineItemStringValue: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], defaultValue?: string) => string;
    getIgnoredPartStringValue?: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], defaultValue?: string) => string;
    lineItemBackendIndexByCurrentIndex: (number | undefined)[];
    lineItemsUseDirectIndex: boolean;
    savedFieldsRef: React.MutableRefObject<Partial<InvoiceDetailResponse>>;
    isMutatingRef: React.MutableRefObject<boolean>;
    updateInvoiceMutation: {
        mutate: (
            input: import('./useUpdateInvoice').UpdateInvoiceInput,
            opts?: { onSuccess?: () => void },
        ) => void;
    };
    suggestionModal: { partIndex: number; suggestedPartNumber: string } | null;
    setSuggestionModal: React.Dispatch<React.SetStateAction<{ partIndex: number; suggestedPartNumber: string } | null>>;
    vendorSuggestionModal: { suggestedVendorName: string } | null;
    setVendorSuggestionModal: React.Dispatch<React.SetStateAction<{ suggestedVendorName: string } | null>>;
    /** After user selects a NEX vendor from the mismatch modal and the invoice save succeeds. */
    onAfterVendorMismatchResolved?: (ctx: {
        nexVendorName: string;
        invoiceVendorSnapshot: string;
        internalSupplierId?: string | null;
        nexVendorId?: number;
    }) => void;
    /** Live line-item arithmetic issues from the form (before save). */
    localLineArithmeticIssues?: Array<{
        partIndex: number;
        partNumber: string;
        issueType: 'calculation_mismatch';
        issueDetails: {
            invoiceQty?: number;
            invoiceUnitPrice?: number;
            invoiceAmount?: number;
            calculatedAmount?: number;
        };
    }>;
    /** Org settings: hide Amount/Qty/Price issue families when disabled. */
    mismatchFlagFamilyPolicy?: InvoiceMismatchFlagFamilyPolicy;
}

export function useInvoiceDetailReconciliationState(
    id: string | undefined,
    invoice: InvoiceDetailResponse | undefined,
    form: FormStateForReconciliation
) {
    const queryClient = useQueryClient();
    const [partNumberChoiceModal, setPartNumberChoiceModal] = useState<{ partIndex: number; issue: any } | null>(null);
    const [reconciliationDetailModal, setReconciliationDetailModal] = useState<ReconciliationDetailModalState>(null);
    const [selectedNexSuggestionIndex, setSelectedNexSuggestionIndex] = useState(0);
    const [selectedNexOptionFromAccordion, setSelectedNexOptionFromAccordion] = useState<InvoicePart | null>(null);
    const [selectedNexOptionKey, setSelectedNexOptionKey] = useState<string | null>(null);
    const [selectedNexOptionAccordionKey, setSelectedNexOptionAccordionKey] = useState<string | null>(null);
    const [expandedMatchedNexRows, setExpandedMatchedNexRows] = useState<Set<string>>(() => new Set());
    const [expandedMatchedNexIgnoredRows, setExpandedMatchedNexIgnoredRows] = useState<Set<number>>(() => new Set());
    const [unmatchedPartIndices, setUnmatchedPartIndices] = useState<Set<number>>(() => new Set());
    const [localMatchedNexOverrides, setLocalMatchedNexOverrides] = useState<Record<number, NexDetailLine>>({});
    const [localReconciliationMatchNotes, setLocalReconciliationMatchNotes] = useState<
        Record<number, ReconciliationMatchNote>
    >({});
    const [previouslyMatchedNexLines, setPreviouslyMatchedNexLines] = useState<Record<number, NexDetailLine>>({});
    const [unmatchConfirmModal, setUnmatchConfirmModal] = useState<{
        partIndex: number;
        partNumber?: string;
        nexPartNumber?: string;
        nexLine?: NexDetailLine;
    } | null>(null);
    const reconciliationModalOpenRef = useRef(false);
    const initializedUnmatchedRef = useRef<string | null>(null);
    const prevBackendUnmatchedRef = useRef<string>('');
    const prevFlaggedModalInitPartIndexRef = useRef<number | null>(null);

    const buildPreviouslyMatchedFromInvoice = (inv: InvoiceDetailResponse | undefined): Record<number, NexDetailLine> => {
        const arr = inv?.reconciliationIssues?.matchedNexLines ?? [];
        return Object.fromEntries(arr.map((m) => [m.partIndex, m.nexLine]));
    };

    useEffect(() => {
        if (!invoice || !id) return;
        if (initializedUnmatchedRef.current === id) return;
        initializedUnmatchedRef.current = id;
        if (invoice.unmatchedPartIndices?.length) {
            setUnmatchedPartIndices(new Set(invoice.unmatchedPartIndices));
        } else {
            setUnmatchedPartIndices(new Set());
        }
        setLocalMatchedNexOverrides(
            Object.fromEntries((invoice.manualMatches ?? []).map((m) => [m.partIndex, m.nexLine]))
        );
        setLocalReconciliationMatchNotes({});
        setPreviouslyMatchedNexLines(buildPreviouslyMatchedFromInvoice(invoice));
    }, [invoice, id]);

    useEffect(() => {
        if (!invoice) return;
        const backend = JSON.stringify(invoice.unmatchedPartIndices ?? []);
        if (prevBackendUnmatchedRef.current !== '' && prevBackendUnmatchedRef.current !== backend) {
            setUnmatchedPartIndices(new Set(invoice.unmatchedPartIndices ?? []));
        }
        prevBackendUnmatchedRef.current = backend;
        setPreviouslyMatchedNexLines(buildPreviouslyMatchedFromInvoice(invoice));
        const serverNotes =
            (
                invoice.reconciliationIssues as
                    | { reconciliationMatchNotes?: Array<{ partIndex: number }> }
                    | undefined
            )?.reconciliationMatchNotes ?? [];
        if (serverNotes.length === 0) return;
        setLocalReconciliationMatchNotes((prev) => {
            if (Object.keys(prev).length === 0) return prev;
            const next = { ...prev };
            let changed = false;
            for (const note of serverNotes) {
                if (note.partIndex in next) {
                    delete next[note.partIndex];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [invoice]);

    const getIssuesForPart = useCallback((index: number) => {
        const backendIssues = omitReturnSlipAmountFlags(
            invoice?.reconciliationIssues?.flaggedParts,
            form.editedFields.invoiceType ?? invoice?.invoiceType,
        );
        const lineCount = (form.editedFields.lineItems ?? invoice?.lineItems ?? []).length;
        let backendMatches: any[];
        // Freight tab uses negative partIndex values for missing_in_nex issues
        // (see UI: `issue.partIndex === -(index + 1)`).
        // For negative indices, we must bypass the line-item index mapping logic.
        if (index < 0) {
            backendMatches = backendIssues.filter((i: { partIndex: number }) => i.partIndex === index);
        } else if (index < lineCount && form.lineItemsUseDirectIndex) {
            backendMatches = backendIssues.filter((i: { partIndex: number }) => i.partIndex === index);
        } else if (index < lineCount && !form.lineItemsUseDirectIndex) {
            const backendIdx = form.lineItemBackendIndexByCurrentIndex[index];
            if (backendIdx == null) {
                backendMatches = [];
            } else {
                backendMatches = backendIssues.filter((i: { partIndex: number }) => i.partIndex === backendIdx);
            }
        } else {
            // Freight/fuel/restocking/discount/ignored use global unified partIndex (matches API).
            backendMatches = backendIssues.filter((i: { partIndex: number }) => i.partIndex === index);
        }

        // Local arithmetic uses unified global partIndex (line items, then freight, fuel, …).
        // Freight tab calls this with negative API convention (e.g. -1 for first freight row); merge
        // live calculation_mismatch issues for the matching global index.
        const localArithmetic = (form.localLineArithmeticIssues ?? []).filter((i) => {
            if (i.partIndex === index) return true;
            if (index < 0) {
                const freightLocalIndex = -(index + 1);
                const globalFromFreightNegative = lineCount + freightLocalIndex;
                return i.partIndex === globalFromFreightNegative;
            }
            return false;
        });
        const combined = [...backendMatches, ...localArithmetic];
        const seen = new Set<string>();
        const deduped: any[] = [];
        for (const issue of combined) {
            if (
                form.mismatchFlagFamilyPolicy &&
                !isIssueTypeFlaggingEnabled(issue?.issueType, form.mismatchFlagFamilyPolicy)
            ) {
                continue;
            }
            const key = JSON.stringify({
                issueType: issue?.issueType,
                partIndex: issue?.partIndex,
                issueDetails: issue?.issueDetails ?? null,
            });
            if (seen.has(key)) continue;
            seen.add(key);
            deduped.push(issue);
        }
        return deduped;
    }, [
        invoice,
        form.editedFields.invoiceType,
        form.editedFields.lineItems,
        form.lineItemsUseDirectIndex,
        form.lineItemBackendIndexByCurrentIndex,
        form.localLineArithmeticIssues,
        form.mismatchFlagFamilyPolicy,
    ]);

    const getLineItemExpandKey = (index: number, item: InvoiceDetailResponse['lineItems'][0]) => buildLineItemExpandKey(index, item);

    const getIssueTooltipText = (issue: any, hasLocalMatch?: boolean, asString?: boolean): any => {
        if (!issue) return '';
        switch (issue.issueType) {
            case 'missing_in_nex':
                if (invoice?.invoiceType === 'received' || invoice?.invoiceType === 'sublet') {
                    return hasLocalMatch
                        ? 'Line-match not found in NEX; manually choose the NEX match (...or PO might not be created for this yet).'
                        : 'Line-match not found in NEX; manually choose the NEX match (...or PO might not be created for this yet).';
                }
                return hasLocalMatch
                    ? 'Part not found in NEX purchase order.'
                    : 'Part not found in NEX purchase order. Will be added to NEX without a matching line item.';
            case 'quantity_mismatch':
                return `Quantity mismatch: Invoice shows ${issue.issueDetails.invoiceQty}, NEX shows ${issue.issueDetails.nexQty}`;
            case 'price_mismatch':
                return `Unit price mismatch: Invoice $${issue.issueDetails.invoiceUnitPrice != null ? fmtUsd(issue.issueDetails.invoiceUnitPrice) : '—'} vs NEX $${issue.issueDetails.nexUnitPrice != null ? fmtUsd(issue.issueDetails.nexUnitPrice) : '—'}`;
            case 'amount_mismatch':
            case 'amount_mismatch_multiple_nex_lines':
            case 'amount_mismatch_single_nex_line':
            case 'amount_mismatch_nex_structure': {
                const invAmt = issue.issueDetails?.invoiceAmount != null ? fmtUsd(issue.issueDetails.invoiceAmount) : '—';
                const nexAmt = issue.issueDetails?.nexAmount != null ? fmtUsd(issue.issueDetails.nexAmount) : '—';
                const invQty = issue.issueDetails?.invoiceQty;
                const nexQty = issue.issueDetails?.nexQty;
                const qtyNote = invQty != null && nexQty != null && invQty !== nexQty ? ` (comparing same quantity: invoice qty ${invQty}, NEX amount for ${invQty} unit${invQty !== 1 ? 's' : ''})` : '';
                return `Amount mismatch vs NEX: Invoice $${invAmt} vs NEX $${nexAmt}${qtyNote}`;
            }
            case 'calculation_mismatch':
                return `Calculation error: Quantity (${issue.issueDetails.invoiceQty}) × Unit Price ($${issue.issueDetails.invoiceUnitPrice != null ? fmtUsd(issue.issueDetails.invoiceUnitPrice) : '—'}) = $${issue.issueDetails.calculatedAmount != null ? fmtUsd(issue.issueDetails.calculatedAmount) : '—'}, but invoice shows $${issue.issueDetails.invoiceAmount != null ? fmtUsd(issue.issueDetails.invoiceAmount) : '—'}`;
            case 'list_price_equals_buy_price':
                return `List price ($${issue.issueDetails.listPrice != null ? fmtUsd(issue.issueDetails.listPrice) : '—'}) equals buy price ($${issue.issueDetails.invoiceUnitPrice != null ? fmtUsd(issue.issueDetails.invoiceUnitPrice) : '—'})`;
            case 'list_price_mismatch':
                return `List price mismatch: Invoice $${issue.issueDetails.invoiceListPrice != null ? fmtUsd(issue.issueDetails.invoiceListPrice) : '—'} vs NEX $${issue.issueDetails.nexListPrice != null ? fmtUsd(issue.issueDetails.nexListPrice) : '—'}`;
            case 'discount_below_expected': {
                const expectedPct =
                    issue.issueDetails?.expectedDiscountPercentage != null
                        ? `${(Number(issue.issueDetails.expectedDiscountPercentage) * 100).toFixed(1)}%`
                        : '—';
                const actualPct =
                    issue.issueDetails?.actualDiscountPercentage != null
                        ? `${(Number(issue.issueDetails.actualDiscountPercentage) * 100).toFixed(1)}%`
                        : '—';
                return `Discount below expected: actual ${actualPct} is less than expected ${expectedPct} (list $${issue.issueDetails?.listPrice != null ? fmtUsd(issue.issueDetails.listPrice) : '—'}, buy $${issue.issueDetails?.unitBuyPrice != null ? fmtUsd(issue.issueDetails.unitBuyPrice) : '—'})`;
            }
            case 'part_number_changed':
                return issue.issueDetails?.replacedPartNumber ? `Part number was updated (supersession): ${issue.partNumber} → ${issue.issueDetails.replacedPartNumber}` : 'Part number was updated to match NEX (supersession)';
            case 'different_part_names': {
                if (invoice?.invoiceType === 'received' || invoice?.invoiceType === 'sublet') {
                    return hasLocalMatch
                        ? 'Line-match not found in NEX; manually choose the NEX match (...or PO might not be created for this yet).'
                        : 'Line-match not found in NEX; manually choose the NEX match (...or PO might not be created for this yet).';
                }
                const invPartNum = issue.partNumber || 'unknown';
                const nexPartNum = issue.issueDetails?.nexPartNumber || issue.issueDetails?.suggestedPartNumber || 'unknown';
                const invName = issue.issueDetails?.invoicePartName;
                const nexName = issue.issueDetails?.nexPartName;
                const baseText = hasLocalMatch ? 'Part not found in NEX purchase order.' : 'Part not found in NEX purchase order. Will be added to NEX without a matching line item.';
                if (invName && nexName) {
                    const noteText = `Note: In Motor API search, ${invPartNum} led to "${invName}" and ${nexPartNum} led to "${nexName}".`;
                    if (asString) return `${baseText} ${noteText}`;
                    return (
                        <VStack align="start" spacing={2}>
                            <Text fontSize="sm">{baseText}</Text>
                            <Text fontSize="sm">{noteText}</Text>
                        </VStack>
                    );
                }
                return hasLocalMatch ? 'Part not found in NEX purchase order. Different part type than NEX (per Motor).' : 'Part not found in NEX purchase order. Different part type than NEX (per Motor). Will be added to NEX without a matching line item.';
            }
            case 'different_positions':
                if (invoice?.invoiceType === 'received' || invoice?.invoiceType === 'sublet') {
                    return hasLocalMatch
                        ? 'Line-match not found in NEX; manually choose the NEX match (...or PO might not be created for this yet).'
                        : 'Line-match not found in NEX; manually choose the NEX match (...or PO might not be created for this yet).';
                }
                return issue.issueDetails?.invoicePartName && issue.issueDetails?.nexPartName
                    ? `Different position: Invoice part in Motor "${issue.issueDetails.invoicePartName}" vs closest NEX match in Motor "${issue.issueDetails.nexPartName}"`
                    : 'Different position than NEX (e.g. left vs right, per Motor)';
            case 'differing_qualifiers':
                return issue.issueDetails?.invoicePartName && issue.issueDetails?.nexPartName ? `Part numbers differ. Invoice part in Motor "${issue.issueDetails.invoicePartName}" vs closest NEX match in Motor "${issue.issueDetails.nexPartName}"; please choose the correct part.` : 'Part numbers differ; please choose the correct part (invoice vs NEX).';
            default:
                return 'Reconciliation issue';
        }
    };

    const getIssueTooltip = (issues: any[], hasLocalMatch?: boolean) => {
        if (!issues?.length) return '';
        if (issues.length === 1) return getIssueTooltipText(issues[0], hasLocalMatch);
        return (
            <VStack align="start" spacing={1}>
                {issues.map((issue, idx) => (
                    <Text key={idx} fontSize="sm">{idx + 1}. {getIssueTooltipText(issue, hasLocalMatch)}</Text>
                ))}
            </VStack>
        );
    };

    const getPartNumberForGlobalIndex = useCallback(
        (partIndex: number): string => {
            const loc = resolveTabularLocationForGlobalPartIndex(partIndex, invoice, form.editedFields);
            if (loc?.kind === 'ignored' && form.getIgnoredPartStringValue) {
                return form.getIgnoredPartStringValue(loc.localIndex, 'partNumber', '').trim().toLowerCase();
            }
            if (loc?.kind === 'line') {
                return form.getLineItemStringValue(loc.localIndex, 'partNumber', '').trim().toLowerCase();
            }
            const lineItems = form.editedFields.lineItems ?? invoice?.lineItems ?? [];
            if (loc?.kind === 'freight') {
                const arr = form.editedFields.freightItems ?? invoice?.freightItems ?? [];
                return String(arr[loc.localIndex]?.partNumber ?? '').trim().toLowerCase();
            }
            if (loc?.kind === 'fuel') {
                const arr = form.editedFields.fuelItems ?? invoice?.fuelItems ?? [];
                return String(arr[loc.localIndex]?.partNumber ?? '').trim().toLowerCase();
            }
            if (loc?.kind === 'restocking') {
                const arr = form.editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
                return String(arr[loc.localIndex]?.partNumber ?? '').trim().toLowerCase();
            }
            if (loc?.kind === 'discount') {
                const arr = form.editedFields.discountItems ?? invoice?.discountItems ?? [];
                return String(arr[loc.localIndex]?.partNumber ?? '').trim().toLowerCase();
            }
            if (partIndex >= 0 && partIndex < lineItems.length) {
                return form.getLineItemStringValue(partIndex, 'partNumber', '').trim().toLowerCase();
            }
            return '';
        },
        [invoice, form]
    );

    const getMissingInNexSuggestion = (partIndex: number): string | undefined => {
        const issues = getIssuesForPart(partIndex);
        const missing = issues.find((i: any) => i.issueType === 'missing_in_nex' && i.issueDetails?.suggestedPartNumber);
        if (!missing) return undefined;
        const current = getPartNumberForGlobalIndex(partIndex);
        const suggested = (missing.issueDetails.suggestedPartNumber as string).trim().toLowerCase();
        if (current === suggested) return undefined;
        return missing.issueDetails.suggestedPartNumber;
    };

    const getPartNumberChoiceIssue = useCallback((partIndex: number): any => {
        const issues = getIssuesForPart(partIndex);
        const issue = issues.find((i: any) => (i.issueType === 'differing_qualifiers' || i.issueType === 'different_part_names' || i.issueType === 'different_positions') && i.issueDetails?.suggestedPartNumber) ?? null;
        if (!issue) return null;
        const current = getPartNumberForGlobalIndex(partIndex);
        const suggested = (issue.issueDetails.suggestedPartNumber as string).trim().toLowerCase();
        if (current === suggested) return null;
        return issue;
    }, [getIssuesForPart, getPartNumberForGlobalIndex]);

    const openReconciliationDetailModal = (modal: ReconciliationDetailModalState) => {
        reconciliationModalOpenRef.current = true;
        setReconciliationDetailModal(modal);
    };
    const closeReconciliationDetailModal = () => {
        reconciliationModalOpenRef.current = false;
        setReconciliationDetailModal(null);
        setSelectedNexOptionFromAccordion(null);
    };

    useEffect(() => {
        if (reconciliationDetailModal?.type === 'flag' && reconciliationDetailModal.partIndex != null) {
            // Guard against re-initializing selection state on re-renders (e.g. after clicking an option).
            if (prevFlaggedModalInitPartIndexRef.current === reconciliationDetailModal.partIndex) return;
            prevFlaggedModalInitPartIndexRef.current = reconciliationDetailModal.partIndex;

            const choiceIssue = getPartNumberChoiceIssue(reconciliationDetailModal.partIndex);
            const issues = getIssuesForPart(reconciliationDetailModal.partIndex);
            const hasMissing = issues.some((i: any) =>
                (i.issueType === 'missing_in_nex' || i.issueType === 'different_part_names' || i.issueType === 'different_positions') &&
                (i.issueDetails?.suggestedPartNumber || i.issueDetails?.suggestedNexLinesFromVendor?.length || i.issueDetails?.suggestedLine || i.issueDetails?.nexOptionsByVendor?.length)
            );
            if (choiceIssue || hasMissing) setSelectedNexSuggestionIndex(0);
            setSelectedNexOptionFromAccordion(null);
            setSelectedNexOptionKey(null);
            setSelectedNexOptionAccordionKey(null);
        } else {
            // Modal closed / switched away: allow re-initialization next time it opens.
            prevFlaggedModalInitPartIndexRef.current = null;
        }
    }, [reconciliationDetailModal?.type, reconciliationDetailModal?.partIndex, getIssuesForPart, getPartNumberChoiceIssue]);

    const closePartNumberChoiceModal = () => setPartNumberChoiceModal(null);
    const handleUseNexPart = () => {
        if (!partNumberChoiceModal || !id || !invoice) return;
        const { partIndex, issue } = partNumberChoiceModal;
        const suggested = issue?.issueDetails?.suggestedPartNumber;
        if (!suggested) return;
        closePartNumberChoiceModal();
        const lineItems = form.editedFields.lineItems ?? invoice.lineItems ?? [];
        const updated = lineItems.map((item: any, i: number) => (i === partIndex ? { ...item, partNumber: suggested } : item));
        form.updateLineItem(partIndex, 'partNumber', suggested);
        form.isMutatingRef.current = true;
        form.savedFieldsRef.current = { ...form.editedFields, lineItems: updated };
        form.updateInvoiceMutation.mutate({ invoiceId: id, updatedFields: { lineItems: updated } });
    };

    const closeSuggestionModal = () => form.setSuggestionModal(null);
    const handleAcceptSuggestion = () => {
        if (!form.suggestionModal || !id || !invoice) return;
        const { partIndex, suggestedPartNumber } = form.suggestionModal;
        closeSuggestionModal();
        const lineItems = form.editedFields.lineItems ?? invoice.lineItems ?? [];
        const updated = lineItems.map((item: any, i: number) => (i === partIndex ? { ...item, partNumber: suggestedPartNumber } : item));
        form.updateLineItem(partIndex, 'partNumber', suggestedPartNumber);
        form.isMutatingRef.current = true;
        form.savedFieldsRef.current = { ...form.editedFields, lineItems: updated };
        form.updateInvoiceMutation.mutate({ invoiceId: id, updatedFields: { lineItems: updated } });
    };

    const closeVendorSuggestionModal = () => form.setVendorSuggestionModal(null);
    const handleAcceptVendorSuggestion = () => {
        if (!form.vendorSuggestionModal || !id) return;
        const { suggestedVendorName } = form.vendorSuggestionModal;
        closeVendorSuggestionModal();
        form.updateField('supplierText', suggestedVendorName);
        form.isMutatingRef.current = true;
        form.savedFieldsRef.current = { ...form.editedFields, supplierText: suggestedVendorName };
        form.updateInvoiceMutation.mutate({ invoiceId: id, updatedFields: { supplierText: suggestedVendorName } });
    };
    const handleAcceptVendorFromMismatch = (vendorName: string) => {
        if (!id || !vendorName || !vendorName.trim()) return;
        const trimmedVendor = vendorName.trim();
        const invoiceVendorSnapshot = (form.editedFields.supplierText ?? invoice?.supplierText ?? '').trim();
        form.updateField('supplierText', trimmedVendor);
        form.isMutatingRef.current = true;
        form.savedFieldsRef.current = { ...form.editedFields, supplierText: trimmedVendor };
        form.updateInvoiceMutation.mutate(
            { invoiceId: id, updatedFields: { supplierText: trimmedVendor } },
            {
                onSuccess: async () => {
                    const effectiveType =
                        form.editedFields.invoiceType ?? invoice?.invoiceType ?? 'received';
                    if (effectiveType === 'return_slip') return;

                    await queryClient.refetchQueries({ queryKey: ['/invoice/detail', id] });
                    const fresh = queryClient.getQueryData<InvoiceDetailResponse>(['/invoice/detail', id]);
                    const nexMeta = findNexPoVendorMetaForName(
                        fresh?.nexResponse ?? invoice?.nexResponse,
                        trimmedVendor
                    );
                    form.onAfterVendorMismatchResolved?.({
                        nexVendorName: trimmedVendor,
                        invoiceVendorSnapshot,
                        internalSupplierId:
                            fresh?.internalSupplierId ?? invoice?.internalSupplierId ?? null,
                        nexVendorId: nexMeta?.vendorId,
                    });
                },
            }
        );
    };

    return {
        partNumberChoiceModal,
        setPartNumberChoiceModal,
        reconciliationDetailModal,
        selectedNexSuggestionIndex,
        setSelectedNexSuggestionIndex,
        selectedNexOptionFromAccordion,
        setSelectedNexOptionFromAccordion,
        selectedNexOptionKey,
        setSelectedNexOptionKey,
        selectedNexOptionAccordionKey,
        setSelectedNexOptionAccordionKey,
        expandedMatchedNexRows,
        setExpandedMatchedNexRows,
        expandedMatchedNexIgnoredRows,
        setExpandedMatchedNexIgnoredRows,
        unmatchedPartIndices,
        setUnmatchedPartIndices,
        localMatchedNexOverrides,
        setLocalMatchedNexOverrides,
        localReconciliationMatchNotes,
        setLocalReconciliationMatchNotes,
        previouslyMatchedNexLines,
        setPreviouslyMatchedNexLines,
        unmatchConfirmModal,
        setUnmatchConfirmModal,
        getIssuesForPart,
        getLineItemExpandKey,
        getIssueTooltipText,
        getIssueTooltip,
        getMissingInNexSuggestion,
        getPartNumberChoiceIssue,
        openReconciliationDetailModal,
        closeReconciliationDetailModal,
        closePartNumberChoiceModal,
        handleUseNexPart,
        closeSuggestionModal,
        handleAcceptSuggestion,
        closeVendorSuggestionModal,
        handleAcceptVendorSuggestion,
        handleAcceptVendorFromMismatch,
    };
}
